import { Lightning, Registry } from '@lightningjs/sdk';
import BillboardProgressBar from 'components/common/progressBar/BillboardProgressBar';
import { LoginLandingLane } from 'services/cwData';
import { CarouselWithSpeech } from 'components/common/CollectionWrappersWithSpeech';
import Router from '@lightningjs/sdk/src/Router';
import LoginLandingBillboard from 'components/pages/login/login-landing/LoginLandingBillboard';

export interface BillboardCarouselTemplateSpec
  extends Lightning.Component.TemplateSpec {
  lane: LoginLandingLane | null;
  Content: {
    BillboardCarousel: typeof CarouselWithSpeech;
    BillboardIndicators: Lightning.Component;
  };
}

const CAROUSEL_WIDTH = 1920;
const CAROUSEL_HEIGHT = 1080;

const ROW_HEIGHT = 601;

const BILLBOARD_INDICATOR_X = 1030;
const BILLBOARD_INDICATOR_Y = 1008;
const BILLBOARD_INDICATOR_W = 820;
const BILLBOARD_INDICATOR_MARGIN = 10;

const NEXT_BILLBOARD_MILLISECONDS = 5000;
const NEXT_BILLBOARD_DELAY_MILLISECONDS = 1000;

export default class LoginLandingBillboardCarousel
  extends Lightning.Component<BillboardCarouselTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<BillboardCarouselTemplateSpec>
{
  private _lane: BillboardCarouselTemplateSpec['lane'] = null;
  private _pauseAutoScroll = false;
  private _autoScrollOnResume = false;
  private _isSinglePromo = false;
  private _hasNavbar = true;
  private _pageTitle = '';

  private _Content = this.getByRef('Content')!;
  private _BillboardCarousel = this._Content.getByRef('BillboardCarousel')!;
  private _BillboardIndicators = this._Content.getByRef('BillboardIndicators')!;

  private nextBillboardTimeoutId: number | null = null;

  get lane() {
    return this._lane;
  }

  set lane(lane: LoginLandingLane | null) {
    this._BillboardIndicators.visible = !!lane && lane.items.length > 1;
    this._pauseAutoScroll = !this._BillboardIndicators.visible;
    this._lane = lane;
    this.updateBillboards();
  }

  set hasNavbar(hasNavbar: boolean) {
    this._hasNavbar = hasNavbar;
  }

  set pageTitle(pageTitle: string) {
    this._pageTitle = pageTitle;
  }

  static get height() {
    return ROW_HEIGHT;
  }

  static override _template(): Lightning.Component.Template<BillboardCarouselTemplateSpec> {
    return {
      Content: {
        BillboardCarousel: {
          w: CAROUSEL_WIDTH,
          h: CAROUSEL_HEIGHT,
          type: CarouselWithSpeech,
          flex: { direction: 'column' },
          flexItem: { marginRight: 10 },
          itemType: LoginLandingBillboard,
          scroll: 0,
          signals: {
            onIndexChanged: '_onIndexChanged',
          },
        },
        BillboardIndicators: {
          x: BILLBOARD_INDICATOR_X,
          y: BILLBOARD_INDICATOR_Y,
          w: BILLBOARD_INDICATOR_W,
          flex: {
            direction: 'row',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
          },
        },
      },
    };
  }

  override _getFocused() {
    return this._BillboardCarousel;
  }

  override _captureRight(e: KeyboardEvent) {
    if (!this._isSinglePromo) return false;

    return true;
  }
  override _captureLeft(e: KeyboardEvent) {
    if (!this._isSinglePromo) return false;

    if (this._hasNavbar) {
      Router.focusWidget('NavBar');
    }
    return true;
  }

  override _active() {
    window.backgroundingService.addBackgroundingAction(
      'billboardBackgrounded',
      this._onBackgrounded.bind(this),
    );
    window.backgroundingService.addForegroundingAction(
      'billboardForegrounded',
      this._onForegrounded.bind(this),
    );
    if (this._BillboardIndicators.visible) {
      this._pauseAutoScroll = false;
      this.setBillboardIndicatorSelected(this._BillboardCarousel.index, true);
    }
  }

  override _inactive() {
    window.backgroundingService.removeBackgroundingAction(
      'billboardBackgrounded',
    );
    window.backgroundingService.removeForegroundingAction(
      'billboardForegrounded',
    );
    this.resetTimeoutId();
    this.setBillboardIndicatorSelected(this._BillboardCarousel.index, false);
  }

  private _onBackgrounded() {
    this._pauseAutoScroll = true;
  }

  private _onForegrounded() {
    if (!this._BillboardIndicators.visible) return;

    this._pauseAutoScroll = false;
    if (this._autoScrollOnResume) {
      this._autoScrollOnResume = false;

      // needs to delay this as calling right too early causes animations to be skipped,
      // billboard indicators would be completely filled unintentionally.
      this.nextBillboardTimeoutId = Registry.setTimeout(
        this.setNextBillboard.bind(this),
        NEXT_BILLBOARD_DELAY_MILLISECONDS,
      );
    }
  }

  private _onIndexChanged(event: { index: number; previousIndex: number }) {
    if (this._isSinglePromo) return;

    this.resetTimeoutId();
    this.setBillboardIndicatorSelected(event.index, true);
    this.setBillboardIndicatorSelected(event.previousIndex, false);
  }

  private updateBillboards() {
    if (!this.lane) return;

    this._isSinglePromo = this.lane.items.length === 1;
    this._BillboardCarousel.items = this.lane.items.map((item, index) => ({
      type: LoginLandingBillboard,
      isSinglePromo: this._isSinglePromo,
      mediaItem: item,
      carouselIndex: index,
      notifyWhenFullyVisible: true,
    }));

    if (this._BillboardIndicators.visible) {
      this._BillboardIndicators.children = this.lane.items.map(() => ({
        type: BillboardProgressBar,
        duration: NEXT_BILLBOARD_MILLISECONDS / 1000,
        delay: NEXT_BILLBOARD_DELAY_MILLISECONDS / 1000,
        flexItem: { marginRight: BILLBOARD_INDICATOR_MARGIN },
      }));
    }
  }

  private setBillboardIndicatorSelected(index: number, selected: boolean) {
    if (index < 0 || !this._BillboardIndicators.visible) return;
    this._BillboardIndicators.childList
      .getAt(index)
      ?.patch({ selected: selected });

    if (selected) {
      this.resetTimeoutId();
      this.nextBillboardTimeoutId = Registry.setTimeout(
        this.setNextBillboard.bind(this),
        NEXT_BILLBOARD_MILLISECONDS + NEXT_BILLBOARD_DELAY_MILLISECONDS,
      );
    }
  }

  private resetTimeoutId() {
    if (this.nextBillboardTimeoutId !== null) {
      Registry.clearTimeout(this.nextBillboardTimeoutId);
      this.nextBillboardTimeoutId = null;
    }
  }

  private setNextBillboard() {
    this.nextBillboardTimeoutId = null;
    this.autoScrollRight();
  }

  private autoScrollRight() {
    if (Router.isNavigating()) return;
    if (this._pauseAutoScroll) {
      this._autoScrollOnResume = true;
      return;
    }
    this._BillboardCarousel.right();
  }
}
